// /about — founder story (D3.3). Photo + LinkedIn stubbed for founder to fill.
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { Mail, Linkedin, MessageCircle } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="About Lectura — Meet Founder Nicole Kaminski"
        description="Meet Nicole Kaminski, founder of Lectura. Built by a student tired of stitching AI tools together. FERPA-ready, COPPA-compliant."
        canonical="https://getlectura.app/about"
      />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-accent">About</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">Built by a student who was tired of tool-hopping.</h1>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
          <div className="grid gap-8 sm:grid-cols-[160px_1fr] items-start">
            <div className="h-40 w-40 rounded-3xl overflow-hidden shadow-lg border border-border/60">
              <img
                src="/founder.webp"
                alt="Nicole Kaminski, founder of Lectura"
                className="h-40 w-40 object-cover"
                loading="eager"
              />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Founder</p>
              <h2 className="mt-1 font-serif text-2xl font-bold">Nicole Kaminski</h2>
              <p className="text-sm text-muted-foreground mt-1">Creator &amp; builder · Lectura</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a href="mailto:hello@getlectura.app" data-testid="about-email-cta" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary/60">
                  <Mail className="h-3.5 w-3.5" /> Email Nicole
                </a>
                <a
                  href="https://www.linkedin.com/in/nicole-kaminski-3bb42b43a"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-linkedin-cta"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary/60"
                  aria-label="Nicole Kaminski on LinkedIn"
                >
                  <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                </a>
                <a
                  href="https://www.reddit.com/user/Lectura26"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-reddit-cta"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary/60"
                  aria-label="Lectura on Reddit — u/Lectura26"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> u/Lectura26
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 prose prose-neutral max-w-none text-foreground/90 leading-relaxed">
            <p>
              Lectura started with a simple frustration: most AI study tools give you one thing at a time. One chatbot for questions. Another for flashcards. A third for summaries. A fourth for slides. Students end up spending more time stitching tools together than actually learning.
            </p>
            <p className="mt-4">
              I wanted a single input — a book and chapter — and a complete study package in return. A real lecture. Slides I could present. A printable guide I could take to the library. A graded quiz that would actually tell me if I understood it. That's Lectura.
            </p>
            <p className="mt-4">
              Every feature is built to help students actually learn, not just get answers. The lecture comes first, the quiz second. Explanations come with every wrong answer. Teachers get classroom tools free because if this helps one classroom of kids master a chapter, the whole thing is worth it.
            </p>
            <p className="mt-4 font-medium text-foreground">
              If Lectura ever stops making you a better student, cancel — the 30-day money-back guarantee is real.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">— Nicole</p>
          </div>

          <div className="mt-10 rounded-2xl border border-border/60 bg-secondary/30 p-6">
            <h3 className="font-bold">The company</h3>
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              <li>Founded: 2026</li>
              <li>Location: United States</li>
              <li>Focus: US K-12 students, teachers, and homeschool families</li>
              <li>Compliance: FERPA-Ready, COPPA-compliant, GDPR-aware</li>
              <li>Model: Gemini 3.1 Pro (Google DeepMind)</li>
            </ul>
          </div>

          <div className="mt-8 text-center">
            <Link to="/register" className="inline-block rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold">
              Try Lectura free
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
